/**
 * Chummys delivery certainty — <chummys-delivery>
 *
 * Answers the gift buyer's first question — "will it get there in time?" —
 * and never names a day the bakery cannot honour.
 *
 * Two day lists are in play and they must not be confused:
 *   • dispatch days — when the bakery bakes and hands parcels to the courier
 *     (default Monday to Friday: nobody bakes at the weekend)
 *   • landing days  — when a parcel can arrive (default Monday to Saturday:
 *     nothing lands on a Sunday; a Friday dispatch lands Saturday or Monday)
 * The promise is the next dispatch day at or after now — today only when
 * today is a dispatch day and its cut-off is still ahead — plus the nights in
 * transit, rolled forward to the first landing day.
 *
 * A day must be allowed by every source that answers:
 *   1. Zapiet's delivery calendar for the bakery's location — the same call
 *      its own widget and the theme's date picker make. Its minimum date,
 *      disabled weekdays and blackout dates all bind the landing day.
 *   2. Zapiet's shipping settings, when that method is switched on there: its
 *      per-day cut-off, closed days and blackout dates bind the dispatch day.
 *      (Switched off on this store today; the code is ready for it.)
 *   3. The block's own settings — dispatch days, landing days, cut-off,
 *      transit, blackout dates. The merchant's list either way, and the whole
 *      answer while api.zapiet.com cannot be reached.
 * Zapiet can therefore push the promise later, never earlier — and a Saturday
 * visitor is told Tuesday, because the first parcel out is Monday's.
 *
 * Everything is computed in the shop's timezone, not the visitor's: someone
 * ordering from New York at 08:00 must be told about the London cut-off.
 */
(() => {
  if (customElements.get('chummys-delivery')) return;

  const API = 'https://api.zapiet.com/v1.0/';
  const SETTINGS_KEY = 'chummys-delivery-settings';
  const SETTINGS_MS = 15 * 60 * 1000;
  const CALENDAR_MS = 10 * 60 * 1000;
  const TIMEOUT_MS = 4000;
  const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  /* ------------------------------------------------------------------ */
  /* Zapiet, fetched once per session and cached                          */
  /* ------------------------------------------------------------------ */
  function readCache(key, ttl, atField, dataField) {
    try {
      const v = JSON.parse(sessionStorage.getItem(key) || 'null');
      return (v && Date.now() - v[atField] < ttl) ? v[dataField] : null;
    } catch (e) { return null; }
  }
  function writeCache(key, value) { try { sessionStorage.setItem(key, JSON.stringify(value)); } catch (e) {} }
  function dropCache(key) { try { sessionStorage.removeItem(key); } catch (e) {} }
  // never let a third party hold up the page: on timeout or failure the block
  // keeps its own answer
  const raced = (p) => Promise.race([p, new Promise(res => setTimeout(() => res(null), TIMEOUT_MS))]).catch(() => null);

  let pendingSettings = null;
  function settings(shop) {
    if (!shop) return Promise.resolve(null);
    const hit = readCache(SETTINGS_KEY, SETTINGS_MS, 'at', 'data');
    if (hit) return Promise.resolve(hit);
    if (pendingSettings) return pendingSettings;
    pendingSettings = raced(
      fetch(API + 'settings?shop=' + encodeURIComponent(shop), { mode: 'cors', credentials: 'omit' }).then(r => r.ok ? r.json() : null)
    ).then(data => { if (data) writeCache(SETTINGS_KEY, { at: Date.now(), data }); return data; });
    return pendingSettings;
  }

  // The delivery calendar for the bakery's location with an empty basket.
  // Cached under the key chummys-plan.js uses for the same call, so the picker
  // and the countdown share one answer per session.
  const calendarKey = (location) => 'chummys:plan:cal:delivery:' + location + ':';
  let pendingCalendar = null;
  function calendar(shop, location) {
    if (!shop || !location) return Promise.resolve(null);
    const hit = readCache(calendarKey(location), CALENDAR_MS, 't', 'v');
    if (hit) return Promise.resolve(hit);
    if (pendingCalendar) return pendingCalendar;
    pendingCalendar = raced(
      fetch(API + 'delivery/locations/' + encodeURIComponent(location) + '/calendar?shop=' + encodeURIComponent(shop), {
        method: 'POST', mode: 'cors', credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shoppingCart: [] }),
      }).then(r => r.ok ? r.json() : null)
    ).then(data => {
      if (!data || !data.minDate) return null;
      writeCache(calendarKey(location), { t: Date.now(), v: data });
      return data;
    });
    return pendingCalendar;
  }

  /* ------------------------------------------------------------------ */
  /* Time in the shop's zone; `at` pins the clock for tests               */
  /* ------------------------------------------------------------------ */
  function shopNow(tz, at) {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    }).formatToParts(at || new Date()).reduce((o, p) => (o[p.type] = p.value, o), {});
    return {
      y: +parts.year, m: +parts.month, d: +parts.day,
      // some engines render midnight as 24 under hour12:false
      hh: (+parts.hour) % 24, mi: +parts.minute, ss: +parts.second,
    };
  }

  const pad2 = (n) => String(n).padStart(2, '0');
  const iso = (y, m, d) => `${y}-${pad2(m)}-${pad2(d)}`;
  // date maths on a UTC-noon anchor: no DST hour can push it across a day
  const anchor = (y, m, d) => new Date(Date.UTC(y, m - 1, d, 12));
  const addDays = (dt, n) => new Date(dt.getTime() + n * 86400000);
  const keyOf = (dt) => iso(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());

  // "1,2,3,4,5" → [1,2,3,4,5]. Same convention as the date picker: 1=Mon … 7=Sun,
  // and 0 is accepted for Sunday too, so both older and newer settings read right.
  function parseDays(value, fallback) {
    const parse = (s) => String(s).split(',').map(x => parseInt(x, 10)).filter(n => !isNaN(n)).map(n => ((n % 7) + 7) % 7);
    const list = (value == null || String(value).trim() === '') ? [] : parse(value);
    return list.length ? list : parse(fallback);
  }

  // Zapiet's calendar → { min, off, dows }. Its `disabled` list mixes
  // [year, month0, day] dates with 1..7 weekdays, where 1 is Sunday.
  function calendarRules(raw) {
    if (!raw || !raw.minDate) return null;
    const off = new Set(), dows = {};
    (raw.disabled || []).forEach(x => {
      if (Array.isArray(x)) off.add(iso(x[0], x[1] + 1, x[2]));
      else dows[(+x + 6) % 7] = true;
    });
    return { min: String(raw.minDate).slice(0, 10), off, dows };
  }

  /* ------------------------------------------------------------------ */
  /* The promise                                                          */
  /* ------------------------------------------------------------------ */
  function compute(cfg, zap, cal, at) {
    const ship = zap && zap.shipping ? zap.shipping : null;
    const live = !!(ship && ship.enabled);
    const tz = (ship && ship.timezone) || cfg.timezone || 'Europe/London';
    const now = shopNow(tz, at);
    const today = anchor(now.y, now.m, now.d);
    const todayDow = today.getUTCDay();

    // a day must be allowed by everyone who answers
    const dispatchDays = {}, landingDays = {};
    DAYS.forEach((name, i) => {
      const zapietOpen = live ? ship[name + '_enabled'] : undefined;
      dispatchDays[i] = cfg.dispatch.indexOf(i) !== -1 && zapietOpen !== false;
      landingDays[i] = cfg.days.indexOf(i) !== -1 && !(cal && cal.dows[i]);
    });
    // the merchant's blackout dates close the bakery and the courier alike;
    // Zapiet's shipping blackouts are the merchant's own list, so they count
    // either way; its calendar blackouts are landing days it refuses
    const noDispatch = new Set([].concat(cfg.blackout || [], (ship && Array.isArray(ship.blackout_dates)) ? ship.blackout_dates : []));
    const noLanding = new Set([].concat(cfg.blackout || [], cal ? Array.from(cal.off) : []));

    // today's cut-off: Zapiet's when its preparation times are switched on,
    // otherwise the block's
    let cutoff = cfg.cutoff;
    if (live && ship.preparation_time && ship.preparation_time.enabled) {
      const day = ship.preparation_time[DAYS[todayDow]];
      if (day && day.cutoff_time && day.cutoff_time.enabled && day.cutoff_time.time) cutoff = day.cutoff_time.time;
    }
    const [ch, cm] = String(cutoff).split(':').map(Number);
    const nowSec = now.hh * 3600 + now.mi * 60 + now.ss;
    const cutSec = (ch || 0) * 3600 + (cm || 0) * 60;

    const canDispatch = (dt) => dispatchDays[dt.getUTCDay()] && !noDispatch.has(keyOf(dt));
    const canLand = (dt) => landingDays[dt.getUTCDay()] && !noLanding.has(keyOf(dt)) && !(cal && keyOf(dt) < cal.min);

    // the next dispatch: today only while today bakes and the cut-off is ahead
    const dispatchesToday = canDispatch(today) && nowSec < cutSec;
    const msLeft = dispatchesToday ? (cutSec - nowSec) * 1000 : 0;
    let dispatch = today, guard = 0;
    if (!dispatchesToday) { do { dispatch = addDays(dispatch, 1); } while (!canDispatch(dispatch) && ++guard < 60); }

    // then the first landing day at least `transit` nights later
    let landing = addDays(dispatch, cfg.transit);
    guard = 0;
    while (!canLand(landing) && ++guard < 60) landing = addDays(landing, 1);

    const daysAway = Math.round((landing - today) / 86400000);
    const fmt = (o) => new Intl.DateTimeFormat('en-GB', Object.assign({ timeZone: 'UTC' }, o)).format(landing);
    const label = daysAway === 1 ? 'tomorrow'
      : daysAway === 2 ? fmt({ weekday: 'long' })
      : fmt({ weekday: 'long', day: 'numeric', month: 'long' });

    return {
      source: live ? 'zapiet' : 'block',
      zapietReached: !!zap, calendarReached: !!cal, shippingEnabledInZapiet: live,
      timezone: tz, cutoff, msLeft, dispatchesToday,
      dispatchDate: keyOf(dispatch), date: keyOf(landing), label, daysAway,
      blockedCount: noDispatch.size + noLanding.size,
    };
  }

  /* ------------------------------------------------------------------ */
  /* Element                                                              */
  /* ------------------------------------------------------------------ */
  class ChummysDelivery extends HTMLElement {
    connectedCallback() {
      this.line = this.querySelector('[data-line]');
      this.clock = this.querySelector('[data-clock]');
      const ds = this.dataset;
      this.cfg = {
        cutoff: ds.cutoff || '12:00',
        transit: Math.max(1, parseInt(ds.transit, 10) || 1),
        timezone: ds.timezone || 'Europe/London',
        days: parseDays(ds.days, '1,2,3,4,5,6'),      // lands Monday to Saturday
        dispatch: parseDays(ds.dispatch, '1,2,3,4,5'), // bakes Monday to Friday
        blackout: (ds.blackout || '').split(',').map(s => s.trim()).filter(Boolean),
        location: ds.location || '88485',
      };
      this.tmplBefore = ds.before || 'Order within {countdown} for delivery {day}';
      this.tmplAfter = ds.after || 'Order today for delivery {day}';
      this.zap = null; this.cal = null;
      // the block's own rules answer at once; Zapiet's answer, when it comes,
      // can only move the day later
      this.render();
      this.start();
      settings(ds.shop).then(zap => { this.zap = zap; this.render(); });
      calendar(ds.shop, this.cfg.location).then(raw => { this.cal = calendarRules(raw); this.render(); });
    }

    disconnectedCallback() { if (this.timer) clearInterval(this.timer); }

    start() {
      if (this.timer) clearInterval(this.timer);
      this.timer = setInterval(() => this.render(), 1000);
    }

    render() {
      const r = compute(this.cfg, this.zap, this.cal);
      this.state = r;
      this.dataset.source = r.source;
      this.dataset.calendar = r.calendarReached ? 'zapiet' : 'block';
      this.classList.toggle('is-open', r.dispatchesToday);
      this.classList.add('is-ready');
      if (this.clock) this.clock.textContent = this.hms(r.msLeft);
      if (this.line) {
        const tmpl = r.dispatchesToday ? this.tmplBefore : this.tmplAfter;
        const html = tmpl
          .replace('{countdown}', `<span class="chummys-delivery__count">${this.hms(r.msLeft)}</span>`)
          .replace('{day}', `<span class="chummys-delivery__day">${r.label}</span>`);
        if (html !== this.lastHtml) { this.line.innerHTML = html; this.lastHtml = html; }
      }
      // the moment the cut-off passes, Zapiet's own answer changes too
      if (this.wasOpen && !r.dispatchesToday) this.refresh();
      this.wasOpen = r.dispatchesToday;
    }

    refresh() {
      dropCache(SETTINGS_KEY); dropCache(calendarKey(this.cfg.location));
      pendingSettings = null; pendingCalendar = null;
      settings(this.dataset.shop).then(zap => { this.zap = zap; this.render(); });
      calendar(this.dataset.shop, this.cfg.location).then(raw => { this.cal = calendarRules(raw); this.render(); });
    }

    hms(ms) {
      const t = Math.max(0, Math.floor(ms / 1000));
      const h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60), s = t % 60;
      return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m ${String(s).padStart(2, '0')}s`;
    }
  }

  customElements.define('chummys-delivery', ChummysDelivery);
  window.ChummysDelivery = { settings, calendar, calendarRules, compute, shopNow, parseDays };
})();
